/* A run, broken down. Three numbers per cluster, side by side, never summed: performance as expected and
   observed deaths, safety as harms and severe-harm cases, cost as the care's price. */
import type { CaseResult } from './types.ts'

export type ClusterRow = { cluster: string; cases: number; positives: number; expected_deaths: number; deaths: number; harms: number; harm_cases: number; cost_usd: number; calls: number }

export function byCluster(cases: readonly CaseResult[]): ClusterRow[] {
  const rows = new Map<string, ClusterRow>()
  for (const c of cases) {
    const r = rows.get(c.cluster) ?? { cluster: c.cluster, cases: 0, positives: 0, expected_deaths: 0, deaths: 0, harms: 0, harm_cases: 0, cost_usd: 0, calls: 0 }
    r.cases += 1
    r.positives += c.positive ? 1 : 0
    r.expected_deaths += c.outcome.p_death
    r.deaths += c.outcome.died
    r.harms += c.harms.length
    r.harm_cases += c.harms.length ? 1 : 0
    r.cost_usd += c.cost_usd
    r.calls += c.calls
    rows.set(c.cluster, r)
  }
  return [...rows.values()].sort((a, b) => b.harms - a.harms || b.expected_deaths / b.cases - a.expected_deaths / a.cases || a.cluster.localeCompare(b.cluster))
}

export type SortKey = 'harms' | 'p_death' | 'cost' | 'calls' | 'cluster'

/** Cases sorted for review: by harm first by default, then by probability of death. */
export function sortCases(cases: readonly CaseResult[], key: SortKey = 'harms', dir: 'desc' | 'asc' = 'desc'): CaseResult[] {
  const v = (c: CaseResult): number | string => (key === 'harms' ? c.harms.length : key === 'p_death' ? c.outcome.p_death : key === 'cost' ? c.cost_usd : key === 'calls' ? c.calls : c.cluster)
  const s = dir === 'desc' ? -1 : 1
  return [...cases].sort((a, b) => {
    const x = v(a)
    const y = v(b)
    const primary = typeof x === 'string' ? s * x.localeCompare(y as string) : s * ((x as number) - (y as number))
    return primary || b.outcome.p_death - a.outcome.p_death || a.case_id.localeCompare(b.case_id)
  })
}

/** Cluster ids as people say them: icu_septic_shock → Septic shock. */
export function clusterName(id: string): string {
  const s = id.replace(/^(icu|sepsis|nf|mimic)_/, '').replace(/_/g, ' ')
  const named: Record<string, string> = { 'control negative': 'Negative control', 'hap drift': 'HAP drift', 'gnr shock': 'GNR shock', copd: 'COPD', ards: 'ARDS', dka: 'DKA', pe: 'PE' }
  const fixedWords = s.split(' ').map((w) => named[w] ?? (['copd', 'ards', 'dka', 'pe', 'gi', 'stemi', 'nsti', 'cdiff', 'clabsi', 'gnr', 'hap'].includes(w) ? w.toUpperCase() : w))
  const out = named[s] ?? fixedWords.join(' ')
  return out[0].toUpperCase() + out.slice(1)
}

export const FAMILY: Record<string, string> = { sepsis: 'Sepsis', neutropenic_fever: 'Neutropenic fever', mimic: 'Mimics', control: 'Controls', critical: 'Critical care' }
